import React from "react";
import styled, { keyframes } from "styled-components";
import { SliderTitleProps } from "./SliderTitle";
import { colors } from "src/theme/colors";
import { radii, space, withAlpha } from "src/theme/ui";

export interface EstimatedAmountProps {
  amount: number | { months: number; years: number };
  className?: string;
  titles: SliderTitleProps;
  /**
   * A new answer is being worked out.
   *
   * The mark belongs IN this box rather than beside it: the figure is the
   * thing going stale, so the sign that it is going stale should be attached
   * to the figure and not floating in the card's margin.
   */
  busy?: boolean;
}

/**
 * The answer.
 *
 * This is the one figure on the page the reader came for — the fourth number,
 * the one the other three are set in order to obtain — and it was a DISABLED
 * INPUT: a green-tinted text box with a green border and greyed-out digits.
 * Three things wrong with that, in order of seriousness.
 *
 * A disabled control invites the interaction it then refuses. It is drawn as
 * a box you type in, so a reader tries, nothing happens, and the page has
 * taught them nothing except that something is broken. A computed value is
 * not an input at all and should not be shaped like one.
 *
 * Greyed-out digits are the convention for "not applicable". The most
 * important number on the page was in the ink the product reserves for
 * things that do not currently matter.
 *
 * And it was green — the colour this product uses for a GAIN. A required
 * contribution of $1,400 a month is not a gain; a duration of six years is
 * not a gain. Tone here would be asserting a direction that the figure does
 * not have.
 *
 * So: a read-out. Named, set large, with a rule down its left edge to say
 * the row is a result rather than another question. Everything above it is
 * what you set; this is what comes back.
 *
 * The GREEN is the old page's, and it is the right one to keep here. It does
 * not mean a gain on this row — a required contribution is not a gain — it
 * means SOLVED, which is how that page used it and the only place on the
 * page anything is. Nothing else in the form is green, so there is no second
 * reading available; and the label says which figure it is, so the colour is
 * never carrying the meaning by itself.
 */
/* Label over figure, the way every other figure in the product is set.
   Label at one end of a wide card and figure at the other left a hand's
   width of blank paper between a thing and its name — the same gap the
   investment page's detail rows were restacked to close. */
const Wrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.md}px;
  padding: 10px 14px;
  border-radius: ${radii.control}px;
  border: 1px solid ${withAlpha(colors.success[500], 0.22)};
  border-left: 3px solid ${colors.success[500]};
  background: ${withAlpha(colors.success[500], 0.06)};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/* In the panel's own colour, so it reads as part of the read-out rather than
   as a grey object that has landed on top of it. */
const Busy = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${withAlpha(colors.success[700], 0.75)};
`;

const Ring = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 1.5px solid ${withAlpha(colors.success[700], 0.22)};
  border-top-color: ${colors.success[700]};
  animation: ${spin} 0.7s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2s;
  }
`;

const Label = styled.span`
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.success[700]};
`;

const Figure = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: 26px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  color: ${colors.success[700]};
`;

/* The unit, set down so the digits carry the line. A duration reads "6 yrs
   4 mo", not as two numbers in two boxes. */
const Unit = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  color: ${withAlpha(colors.success[700], 0.6)};
`;

const money = (value: number) =>
  Number.isFinite(value) ? Math.round(value).toLocaleString("en-US") : "—";

const EstimatedAmount: React.FC<EstimatedAmountProps> = ({
  titles,
  amount,
  className,
  busy,
}) => (
  <Wrap className={className}>
    <Stack>
      <Label>{titles.title}</Label>
      {typeof amount === "number" ? (
        <Figure>
          <Unit>$</Unit>
          {money(amount)}
        </Figure>
      ) : (
        <Figure>
          {amount.years > 0 ? (
            <>
              {amount.years}
              <Unit>{amount.years === 1 ? "yr" : "yrs"}</Unit>
            </>
          ) : null}
          {amount.months > 0 || amount.years === 0 ? (
            <>
              {amount.months}
              <Unit>mo</Unit>
            </>
          ) : null}
        </Figure>
      )}
    </Stack>
    {busy ? (
      <Busy>
        <Ring />
        Updating
      </Busy>
    ) : null}
  </Wrap>
);

export default EstimatedAmount;
